import * as Sentry from "@sentry/vue";

export interface NotifyBootstrapErrorDeps {
  /** Fase di bootstrap che ha fallito (es. "setupLocalization") */
  stage: string;
  error: unknown;
  log: (message: string, error: unknown) => void;
  capture: (error: unknown, stage: string) => void;
}

const BOOTSTRAP_FAILURE_TITLE = "Avvio dell'applicazione non riuscito";
const BOOTSTRAP_FAILURE_HINT =
  "Alcuni servizi non hanno risposto. Riprova; se l'errore persiste segnalalo all'assistenza.";

/**
 * Funzione pura: notifica il fallimento di una fase di bootstrap su entrambi i
 * canali (console + monitoraggio). Estratta per testabilità senza side effect
 * su console e Sentry globali.
 */
export function notifyBootstrapError({
  stage,
  error,
  log,
  capture,
}: NotifyBootstrapErrorDeps): void {
  if (!stage.trim()) throw new Error("notifyBootstrapError: stage is required");

  log(`[Bootstrap] ${stage} failed:`, error);
  capture(error, stage);
}

/**
 * Segnala il fallimento di una fase di avvio (o di un caricamento che non deve
 * far cadere l'app). Da usare in ogni `catch` del boot: un errore ingoiato in
 * avvio si manifesta come pagina bianca senza tracce in Sentry.
 */
export function reportBootstrapError(stage: string, error: unknown): void {
  notifyBootstrapError({
    stage,
    error,
    log: (message, failure) => console.error(message, failure),
    capture: (failure, failedStage) =>
      Sentry.captureException(failure, { tags: { bootstrapStage: failedStage } }),
  });
}

/** `true` se il rifiuto è la richiesta di login di Keycloak, non un errore applicativo. */
export function isLoginRequiredError(error: unknown): boolean {
  return (error as { error?: unknown } | null | undefined)?.error === "login_required";
}

export interface RenderBootstrapFailureDeps {
  container: HTMLElement | null;
  error: unknown;
  reload: () => void;
}

function describeError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return JSON.stringify(error ?? "errore sconosciuto");
}

/**
 * Schermata minima di fallimento del boot, in DOM puro e senza dipendenze.
 *
 * Deroga consapevole a Vuetify (ui-vuetify §2.1): viene disegnata proprio quando
 * l'app Vue non è montabile, quindi né i componenti Vuetify né i18n sono
 * disponibili. Meglio un pannello statico che una pagina bianca muta.
 */
export function renderBootstrapFailure({
  container,
  error,
  reload,
}: RenderBootstrapFailureDeps): void {
  if (!container) throw new Error("renderBootstrapFailure: container is required");

  const panel = document.createElement("div");
  panel.setAttribute("data-testid", "bootstrap-error-panel");
  panel.setAttribute("role", "alert");
  panel.style.cssText =
    "max-width:32rem;margin:15vh auto;padding:1.5rem;border-radius:0.75rem;" +
    "border:1px solid #e0e0e0;font-family:system-ui,sans-serif;color:#212121;background:#fff";

  const title = document.createElement("h1");
  title.textContent = BOOTSTRAP_FAILURE_TITLE;
  title.style.cssText = "margin:0 0 0.5rem;font-size:1.125rem";

  const hint = document.createElement("p");
  hint.textContent = BOOTSTRAP_FAILURE_HINT;
  hint.style.cssText = "margin:0 0 1rem;font-size:0.875rem;color:#616161";

  const detail = document.createElement("pre");
  detail.setAttribute("data-testid", "bootstrap-error-detail");
  detail.textContent = describeError(error);
  detail.style.cssText =
    "margin:0 0 1rem;padding:0.75rem;border-radius:0.5rem;background:#f5f5f5;" +
    "font-size:0.75rem;white-space:pre-wrap;overflow-wrap:anywhere";

  const retry = document.createElement("button");
  retry.setAttribute("data-testid", "bootstrap-error-retry");
  retry.type = "button";
  retry.textContent = "Riprova";
  retry.style.cssText =
    "padding:0.5rem 1rem;border:0;border-radius:0.5rem;background:#212121;color:#fff;cursor:pointer";
  retry.addEventListener("click", () => reload());

  panel.append(title, hint, detail, retry);
  container.replaceChildren(panel);
}
